import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  BarChart3, Users, Package, MessageSquare, Settings, Shield, 
  TrendingUp, Eye, Edit, Trash2, Search, Filter, Download,
  AlertTriangle, CheckCircle, XCircle, Crown, QrCode,
  Calendar, MapPin, Camera, Mail, Globe, Activity, ShoppingBag, Database
} from 'lucide-react';
import api from '../services/api';
import ProductManagement from './ProductManagement';

export const AdminPanel = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState({});
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Mock admin data
  useEffect(() => {
    const loadAdminData = async () => {
      setLoading(true);
      try {
        // Mock API calls with realistic admin data
        setTimeout(() => {
          setStats({
            totalUsers: 3456,
            totalItems: 1247,
            totalStories: 8934,
            totalScans: 45678,
            activeUsers: 892,
            newUsersToday: 23,
            newItemsToday: 8,
            newStoriesToday: 47,
            pendingReports: 3,
            flaggedContent: 5
          });

          setItems([
            { id: 'TM001234', name: 'Adventure Backpack', creator: 'Sarah Johnson', scans: 8, status: 'active', created: '2024-01-15', location: 'Portland, OR' },
            { id: 'TM005678', name: 'Vintage Guitar', creator: 'Tom Miller', scans: 12, status: 'active', created: '2024-03-01', location: 'Nashville, TN' },
            { id: 'TM009876', name: 'Children\'s Book', creator: 'Lisa Park', scans: 15, status: 'active', created: '2024-02-10', location: 'Boston, MA' },
            { id: 'TM012345', name: 'Vintage Camera', creator: 'David Kim', scans: 9, status: 'flagged', created: '2024-01-20', location: 'Miami, FL' },
            { id: 'TM013579', name: 'Friendship Bracelet', creator: 'Maya Patel', scans: 6, status: 'active', created: '2024-03-05', location: 'Phoenix, AZ' }
          ]);

          setUsers([
            { id: 1, name: 'Sarah Johnson', email: 'sarah@email.com', items: 3, stories: 8, joined: '2024-01-15', status: 'active', lastActive: '2 hours ago' },
            { id: 2, name: 'Tom Miller', email: 'tom@email.com', items: 1, stories: 12, joined: '2024-02-20', status: 'active', lastActive: '1 day ago' },
            { id: 3, name: 'Lisa Park', email: 'lisa@email.com', items: 2, stories: 5, joined: '2024-01-08', status: 'active', lastActive: '3 hours ago' },
            { id: 4, name: 'David Kim', email: 'david@email.com', items: 1, stories: 3, joined: '2024-03-12', status: 'suspended', lastActive: '1 week ago' },
            { id: 5, name: 'Maya Patel', email: 'maya@email.com', items: 1, stories: 4, joined: '2024-02-28', status: 'active', lastActive: '5 minutes ago' }
          ]);

          setStories([
            { id: 1, item: 'Adventure Backpack', user: 'Mike Chen', content: 'Found this cool backpack at a hostel!', location: 'Austin, TX', date: '2024-02-03', status: 'approved' },
            { id: 2, item: 'Vintage Guitar', user: 'Jazz Collective', content: 'This beautiful guitar joined our jazz session!', location: 'New Orleans, LA', date: '2024-03-15', status: 'approved' },
            { id: 3, item: 'Children\'s Book', user: 'Public Library', content: 'This wonderful book made it to our library!', location: 'Boston, MA', date: '2024-03-20', status: 'pending' },
            { id: 4, item: 'Lucky Penny', user: 'Casino Worker', content: 'This penny arrived in Sin City!', location: 'Las Vegas, NV', date: '2024-03-15', status: 'flagged' },
            { id: 5, item: 'Love Letter', user: 'Wedding Planner', content: 'This letter arrived in romantic Savannah!', location: 'Savannah, GA', date: '2024-03-22', status: 'approved' }
          ]);

          setLoading(false);
        }, 1000);
      } catch (error) {
        console.error('Error loading admin data:', error);
        setLoading(false);
      }
    };

    loadAdminData();
  }, []);

  const StatCard = ({ icon: Icon, title, value, change, color = "blue" }) => (
    <Card className="hover:shadow-lg transition-shadow">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <p className="text-3xl font-bold text-gray-900">{value}</p>
            {change && (
              <p className={`text-sm ${change.startsWith('+') ? 'text-green-600' : 'text-red-600'}`}>
                {change} from yesterday
              </p>
            )}
          </div>
          <div className={`p-3 bg-${color}-100 rounded-lg`}>
            <Icon className={`w-6 h-6 text-${color}-600`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const Dashboard = () => (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard icon={Users} title="Total Users" value={stats.totalUsers?.toLocaleString()} change="+23 today" color="blue" />
        <StatCard icon={Package} title="Total Items" value={stats.totalItems?.toLocaleString()} change="+8 today" color="emerald" />
        <StatCard icon={MessageSquare} title="Total Stories" value={stats.totalStories?.toLocaleString()} change="+47 today" color="purple" />
        <StatCard icon={QrCode} title="Total Scans" value={stats.totalScans?.toLocaleString()} change="+156 today" color="orange" />
      </div>

      {/* Activity Overview */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="font-medium">New story approved</p>
                    <p className="text-sm text-gray-600">Children's Book - Boston, MA</p>
                  </div>
                </div>
                <span className="text-sm text-gray-500">2m ago</span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="font-medium">New user registered</p>
                    <p className="text-sm text-gray-600">alex@example.com</p>
                  </div>
                </div>
                <span className="text-sm text-gray-500">5m ago</span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Package className="w-5 h-5 text-orange-600" />
                  <div>
                    <p className="font-medium">New item created</p>
                    <p className="text-sm text-gray-600">Friendship Necklace - Miami, FL</p>
                  </div>
                </div>
                <span className="text-sm text-gray-500">8m ago</span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <div>
                    <p className="font-medium">Content flagged</p>
                    <p className="text-sm text-gray-600">Story requires review</p>
                  </div>
                </div>
                <span className="text-sm text-gray-500">15m ago</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Platform Health
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Active Users (24h)</span>
                <Badge className="bg-green-100 text-green-800">{stats.activeUsers}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Pending Reports</span>
                <Badge className="bg-yellow-100 text-yellow-800">{stats.pendingReports}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Flagged Content</span>
                <Badge className="bg-red-100 text-red-800">{stats.flaggedContent}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">System Status</span>
                <Badge className="bg-green-100 text-green-800">Healthy</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Button className="h-20 flex-col gap-2" variant="outline">
              <Eye className="w-6 h-6" />
              Review Content
            </Button>
            <Button className="h-20 flex-col gap-2" variant="outline">
              <Users className="w-6 h-6" />
              Manage Users
            </Button>
            <Button className="h-20 flex-col gap-2" variant="outline">
              <BarChart3 className="w-6 h-6" />
              View Analytics
            </Button>
            <Button className="h-20 flex-col gap-2" variant="outline">
              <Download className="w-6 h-6" />
              Export Data
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const UserManagement = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">User Management</h2>
        <div className="flex gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input placeholder="Search users..." className="pl-10 w-64" />
          </div>
          <Button variant="outline">
            <Filter className="w-4 h-4 mr-2" />
            Filter
          </Button>
          <Button>
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Items</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stories</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Active</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-blue-500 rounded-full flex items-center justify-center">
                          <span className="text-white font-medium">{user.name.charAt(0)}</span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{user.name}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{user.items}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{user.stories}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge className={user.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                        {user.status}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.lastActive}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline">
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="outline">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const ItemManagement = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Item Management</h2>
        <div className="flex gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input placeholder="Search items..." className="pl-10 w-64" />
          </div>
          <Button variant="outline">
            <Filter className="w-4 h-4 mr-2" />
            Filter
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Creator</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Scans</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {items.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Package className="w-8 h-8 text-gray-400 mr-3" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{item.name}</div>
                          <div className="text-sm text-gray-500">{item.id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.creator}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.scans}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge className={
                        item.status === 'active' ? 'bg-green-100 text-green-800' :
                        item.status === 'flagged' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }>
                        {item.status}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.location}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline">
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="outline">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700">
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const ContentModeration = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Content Moderation</h2>
        <div className="flex gap-3">
          <Badge className="bg-red-100 text-red-800">{stories.filter(s => s.status === 'flagged').length} Flagged</Badge>
          <Badge className="bg-yellow-100 text-yellow-800">{stories.filter(s => s.status === 'pending').length} Pending</Badge>
        </div>
      </div>

      <div className="grid gap-6">
        {stories.filter(story => story.status !== 'approved').map((story) => (
          <Card key={story.id} className={`border-l-4 ${
            story.status === 'flagged' ? 'border-red-500' : 'border-yellow-500'
          }`}>
            <CardContent className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="font-bold text-lg">{story.item}</h3>
                  <p className="text-gray-600">by {story.user} • {story.location}</p>
                  <p className="text-sm text-gray-500">{story.date}</p>
                </div>
                <Badge className={
                  story.status === 'flagged' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                }>
                  {story.status}
                </Badge>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-gray-700">{story.content}</p>
              </div>
              
              <div className="flex gap-3">
                <Button size="sm" className="bg-green-600 hover:bg-green-700">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve
                </Button>
                <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700">
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject
                </Button>
                <Button size="sm" variant="outline">
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  const Analytics = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">Analytics & Reports</h2>
      
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard icon={Globe} title="Countries Reached" value="23" change="+2 this month" color="blue" />
        <StatCard icon={MapPin} title="Miles Tracked" value="127K+" change="+2.3K today" color="emerald" />
        <StatCard icon={Camera} title="Photos Uploaded" value="5,234" change="+89 today" color="purple" />
        <StatCard icon={Mail} title="Emails Sent" value="12,456" change="+234 today" color="orange" />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Top Performing Items</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {items.slice(0, 5).map((item, index) => (
                <div key={item.id} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold text-gray-400">#{index + 1}</span>
                    <div>
                      <p className="font-medium">{item.name}</p>
                      <p className="text-sm text-gray-600">{item.creator}</p>
                    </div>
                  </div>
                  <Badge>{item.scans} scans</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Geographic Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span>United States</span>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-2 bg-gray-200 rounded-full">
                    <div className="w-20 h-2 bg-emerald-500 rounded-full"></div>
                  </div>
                  <span className="text-sm">85%</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span>Canada</span>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-2 bg-gray-200 rounded-full">
                    <div className="w-3 h-2 bg-blue-500 rounded-full"></div>
                  </div>
                  <span className="text-sm">10%</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span>Mexico</span>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-2 bg-gray-200 rounded-full">
                    <div className="w-1 h-2 bg-purple-500 rounded-full"></div>
                  </div>
                  <span className="text-sm">5%</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  // Sample Data Management Component
  const SampleDataManagement = () => {
    const [sampleStats, setSampleStats] = useState(null);
    const [loadingSample, setLoadingSample] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);

    const loadSampleStats = async () => {
      setLoadingSample(true);
      try {
        const response = await api.get('/admin/sample-data/stats');
        setSampleStats(response.data);
      } catch (error) {
        console.error('Error loading sample data stats:', error);
      }
      setLoadingSample(false);
    };

    useEffect(() => {
      loadSampleStats();
    }, []);

    const handleRemoveAll = async () => {
      if (!window.confirm('Are you sure you want to remove ALL sample data? This cannot be undone.')) return;
      
      setActionLoading(true);
      try {
        const response = await api.delete('/admin/sample-data/remove-all');
        alert(`Removed ${response.data.deletedCount} sample items`);
        loadSampleStats();
      } catch (error) {
        console.error('Error removing sample data:', error);
        alert('Failed to remove sample data');
      }
      setActionLoading(false);
    };

    const handleRemoveJeepDuck = async () => {
      if (!window.confirm('Are you sure you want to remove all JeepDuck sample items?')) return;
      
      setActionLoading(true);
      try {
        const response = await api.delete('/admin/sample-data/remove-jeepduck');
        alert(`Removed ${response.data.deletedCount} JeepDuck sample items`);
        loadSampleStats();
      } catch (error) {
        console.error('Error removing JeepDuck sample data:', error);
        alert('Failed to remove JeepDuck sample data');
      }
      setActionLoading(false);
    };

    const handleRemoveItem = async (uid, name) => {
      if (!window.confirm(`Remove sample item "${name}"?`)) return;
      
      setActionLoading(true);
      try {
        await api.delete(`/admin/sample-data/remove-item/${uid}`);
        loadSampleStats();
      } catch (error) {
        console.error('Error removing sample item:', error);
        alert('Failed to remove sample item');
      }
      setActionLoading(false);
    };

    if (loadingSample) {
      return (
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Sample Data Management</h2>
            <p className="text-gray-600">Manage placeholder/demo items for club pages</p>
          </div>
          <Button onClick={loadSampleStats} variant="outline" disabled={actionLoading}>
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Sample Items</p>
                  <p className="text-3xl font-bold text-gray-900">{sampleStats?.totalSampleItems || 0}</p>
                </div>
                <Database className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">JeepDuck Samples</p>
                  <p className="text-3xl font-bold text-emerald-600">{sampleStats?.jeepDuckSampleItems || 0}</p>
                </div>
                <Package className="w-8 h-8 text-emerald-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Auto-Removal Active</p>
                  <p className="text-xs text-amber-700">Sample items are removed when real stories are added to JeepDuck items</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Action Buttons */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-3">
              <Button 
                variant="destructive" 
                onClick={handleRemoveJeepDuck}
                disabled={actionLoading || !sampleStats?.jeepDuckSampleItems}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Remove All JeepDuck Samples ({sampleStats?.jeepDuckSampleItems || 0})
              </Button>
              <Button 
                variant="outline" 
                className="border-red-300 text-red-600 hover:bg-red-50"
                onClick={handleRemoveAll}
                disabled={actionLoading || !sampleStats?.totalSampleItems}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Remove ALL Sample Data ({sampleStats?.totalSampleItems || 0})
              </Button>
            </div>
            <p className="text-sm text-gray-500">
              Note: Sample items are automatically removed one-by-one as real users add stories to JeepDuck-tagged items.
            </p>
          </CardContent>
        </Card>

        {/* Sample Items List */}
        {sampleStats?.items && sampleStats.items.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Sample Items ({sampleStats.items.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {sampleStats.items.map((item) => (
                  <div key={item.uid} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div>
                        <p className="font-medium text-gray-900">{item.name}</p>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Badge variant="outline">{item.privateTag}</Badge>
                          <span>{item.storyCount} stories</span>
                          <span>•</span>
                          <span>{new Date(item.created).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleRemoveItem(item.uid, item.name)}
                      disabled={actionLoading}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {sampleStats?.items?.length === 0 && (
          <Card className="bg-gray-50">
            <CardContent className="p-8 text-center">
              <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
              <p className="text-gray-600">No sample data items in the system</p>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-100 z-50 overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Crown className="w-8 h-8 text-yellow-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">TrackMe Admin Panel</h1>
              <p className="text-gray-600">Manage your platform</p>
            </div>
          </div>
          <Button onClick={onClose} variant="outline">
            Close Panel
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex h-[calc(100vh-80px)]">
        {/* Sidebar */}
        <div className="w-64 bg-white border-r border-gray-200 overflow-y-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab} orientation="vertical" className="w-full">
            <TabsList className="grid w-full grid-rows-6 h-auto bg-transparent p-2">
              <TabsTrigger 
                value="dashboard" 
                className="w-full justify-start data-[state=active]:bg-emerald-100 data-[state=active]:text-emerald-700"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Dashboard
              </TabsTrigger>
              <TabsTrigger 
                value="users" 
                className="w-full justify-start data-[state=active]:bg-emerald-100 data-[state=active]:text-emerald-700"
              >
                <Users className="w-4 h-4 mr-2" />
                Users
              </TabsTrigger>
              <TabsTrigger 
                value="items" 
                className="w-full justify-start data-[state=active]:bg-emerald-100 data-[state=active]:text-emerald-700"
              >
                <Package className="w-4 h-4 mr-2" />
                Items
              </TabsTrigger>
              <TabsTrigger 
                value="store" 
                className="w-full justify-start data-[state=active]:bg-emerald-100 data-[state=active]:text-emerald-700"
              >
                <ShoppingBag className="w-4 h-4 mr-2" />
                Store
              </TabsTrigger>
              <TabsTrigger 
                value="moderation" 
                className="w-full justify-start data-[state=active]:bg-emerald-100 data-[state=active]:text-emerald-700"
              >
                <Shield className="w-4 h-4 mr-2" />
                Moderation
              </TabsTrigger>
              <TabsTrigger 
                value="analytics" 
                className="w-full justify-start data-[state=active]:bg-emerald-100 data-[state=active]:text-emerald-700"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Analytics
              </TabsTrigger>
              <TabsTrigger 
                value="sampledata" 
                className="w-full justify-start data-[state=active]:bg-emerald-100 data-[state=active]:text-emerald-700"
              >
                <Database className="w-4 h-4 mr-2" />
                Sample Data
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsContent value="dashboard">
                <Dashboard />
              </TabsContent>
              <TabsContent value="users">
                <UserManagement />
              </TabsContent>
              <TabsContent value="items">
                <ItemManagement />
              </TabsContent>
              <TabsContent value="store">
                <ProductManagement />
              </TabsContent>
              <TabsContent value="moderation">
                <ContentModeration />
              </TabsContent>
              <TabsContent value="analytics">
                <Analytics />
              </TabsContent>
              <TabsContent value="sampledata">
                <SampleDataManagement />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
};